/**
 * client/src/lib/webauthn/chosen-credential.ts — the passkey this person is
 * ACTUALLY using, and the one every later ceremony should ask for first.
 *
 * WHY THIS EXISTS
 * ---------------
 * `local-credential.ts` remembers the last credential that answered from THIS
 * device (`authenticatorAttachment === "platform"`) and offers it alone, so the
 * OS prompt opens instead of a cross-device chooser. That is a latency win, and
 * it is correct as far as it goes — but it can only ever name a LOCAL passkey.
 *
 * An account with a Windows Hello passkey AND an iPhone passkey therefore gets
 * permanently routed to Hello on that laptop: the narrowed list holds one
 * credential, its transports are rewritten to `internal`, and with no `hybrid`
 * entry the browser has no cross-device path to offer. The iPhone passkey the
 * person signs in with elsewhere becomes unreachable in that browser profile,
 * and nothing in the UI says why or offers a way out (2026-08-20 report).
 *
 * The fix is to prefer the credential the person actually CHOSE, whatever
 * device it lives on, over the one that merely happens to be local:
 *
 *   1. WRITTEN AT SIGN-IN AND NOWHERE ELSE. `auth-entry` records it; no other
 *      ceremony does. A step-up or a stamp answers whatever ITS OWN gate
 *      demanded — a hardware key for the AAL3 arm, the sealing key for a
 *      PRF-pinned record — and letting those overwrite the preference would
 *      route every later ceremony to a credential nobody picked for general
 *      use, which is exactly the dead end the old memory avoided by refusing
 *      to record cross-platform answers. It is also the literal ask: the
 *      passkey you opened THIS session with.
 *   2. WHATEVER ATTACHMENT. A phone answering over hybrid is recorded exactly
 *      like a built-in authenticator. This is the whole point — the local
 *      memory already covers the platform case and covers nothing else.
 *   3. ITS REAL TRANSPORTS SURVIVE. `preferLocalCredential` narrows to this
 *      credential using the transports the SERVER offered for it, so a
 *      `hybrid` entry keeps its QR / cross-device route. Rewriting to
 *      `internal` here would recreate the exact lockout described above.
 *   4. IT IS RELEASED BY A FAILED OR DISMISSED PROMPT — but only when the
 *      narrowing actually REMOVED options. A caller-pinned single-entry list
 *      offers the same one credential with or without us, so cancelling a
 *      pinned ceremony says nothing about the preference and must not clear
 *      it. When options WERE removed, a dismissal is the only signal a person
 *      can give that means "not that one", so `local-credential.ts` clears the
 *      narrowing and the next attempt offers everything.
 *
 * SCOPE, HONESTLY. `allowCredentials` is UX routing metadata, never a security
 * input (server `helpers.toAllowCredentials`). Preferring one credential over
 * another changes WHICH key is asked for and never what a key proves: the
 * server verifies the credential-id lookup, account ownership, revocation,
 * origin, challenge, UV and counter regardless of what was offered. Narrowing
 * can only make a ceremony fail closed. It proves possession + freshness,
 * never liveness (CLAUDE.md §Friction Rule clause 2).
 *
 * WHERE IT LIVES. `localStorage`, not `sessionStorage`: the signed-in session
 * is a cookie shared by every tab, and a preference that forgot itself in a
 * second tab would send that tab straight back to the wrong passkey. Per
 * browser-profile, per machine, never synced, never sent to the server, and
 * cleared by `handleLogout` — but NOT by a session that dies another way (idle
 * wipe, zero-trust epoch, forensic pause), so read "cleared on logout" as the
 * explicit sign-out path only. A stale entry, from a dead session or another
 * account, is harmless by construction: it is used ONLY when the id is still in
 * the list the server just offered, and the next sign-in overwrites it.
 */

const STORAGE_KEY = "strenn:chosenCredentialId";

export interface ChosenCredential {
  readonly id: string;
  /**
   * The attachment the browser reported when this credential answered.
   * `"platform"` is the ONLY value that licenses a `client-device` hint — for
   * anything else the credential may well be a phone, and pointing the browser
   * at the built-in authenticator would be pointing it away from the answer.
   */
  readonly attachment: string | null;
}

/** Every access is wrapped — Safari private mode and locked-down profiles throw
 *  on `localStorage`, and a ceremony must never fail over a routing preference. */
function safeRead(): ChosenCredential | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ChosenCredential> | null;
    if (!parsed || typeof parsed.id !== "string" || parsed.id.length === 0) return null;
    return { id: parsed.id, attachment: typeof parsed.attachment === "string" ? parsed.attachment : null };
  } catch {
    return null;
  }
}

/** The credential this person last chose to authenticate with, if any. */
export function readChosenCredential(): ChosenCredential | null {
  return safeRead();
}

/** The two fields recorded off a finished assertion. Structural rather than
 *  `AuthenticationResponseJSON` so a registration response fits it too. */
export interface ChosenCredentialEvidence {
  id?: string;
  authenticatorAttachment?: string | null;
}

/**
 * Record the credential that just answered — at ANY attachment.
 *
 * The deliberate contrast with `rememberLocalCredential`, which records a
 * `"platform"` answer only: that memory is a claim about WHERE a credential
 * lives, so it needs the attachment as evidence. This one is a record of WHAT
 * the person picked, and a phone is as valid a pick as a fingerprint reader.
 */
export function rememberChosenCredential(response: ChosenCredentialEvidence): void {
  if (!response.id) return;
  const value: ChosenCredential = {
    id: response.id,
    attachment: response.authenticatorAttachment ?? null,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* A browser that refuses storage simply keeps today's routing. */
  }
}

/** Drop the preference so the next ceremony offers the full list. Called on a
 *  failed or dismissed narrowed ceremony, and on logout. */
export function forgetChosenCredential(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* a preference we cannot clear is one we could not read either */
  }
}
